import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { HiTrash } from 'react-icons/hi'
import { useApp } from '../../context/AppContext'
import FormError from '../feedback/FormError'
import adminServices from '../../services/AdminServices'
import { RenderIcon } from './IconRenderer'

const ICON_PRESETS = [
    { name: 'BsWifi', label: 'WiFi' },
    { name: 'MdPool', label: 'Pool' },
    { name: 'BsSnow', label: 'AC' },
    { name: 'FaCoffee', label: 'Kitchen' },
    { name: 'IoCar', label: 'Parking' },
    { name: 'MdFitnessCenter', label: 'Gym' },
    { name: 'BsTv', label: 'TV' },
    { name: 'MdLocalLaundryService', label: 'Laundry' },
    { name: 'BsShieldCheck', label: 'Security' },
    { name: 'MdRestaurant', label: 'Dining' },
]

function AmenityModal({ action, activeAmenity, onReload, setActiveAmenity }) {
    const { setAmenities } = useApp()
    const [name, setName] = useState('')
    const [iconClass, setIconClass] = useState('')
    const [description, setDescription] = useState('')

    const [loading, setLoading] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState(0)

    const tabs = useMemo(() => {
        const base = ['Info']
        if (action === 1 || action === 2) base.push('Delete')
        return base
    }, [action])

    useEffect(() => {
        if ((action === 1 || action === 2) && activeAmenity) {
            setName(activeAmenity.name || '')
            setIconClass(activeAmenity.icon_class || '')
            setDescription(activeAmenity.description || '')

        } else {
            setName('')
            setIconClass('')
            setDescription('')
        }
        setActiveTab(0)
    }, [action, activeAmenity])

    const updateSession = () => {
        adminServices.adminListAmenities()
            .then(res => setAmenities(res.data || []))
            .catch(err => console.warn('Session cache update failed', err))
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setError(null)
        setLoading(true)

        const payload = {
            name,
            icon_class: iconClass || '',
            description
        }

        const request = action === 1
            ? adminServices.adminUpdateAmenity(activeAmenity.id, payload)
            : adminServices.adminCreateAmenity(payload)

        request
            .then(res => {
                toast.success(action === 1 ? 'Amenity updated' : 'Amenity created')
                onReload?.()
                updateSession()
                closeModal()
            })
            .catch(err => {
                const msg = err.response?.data?.name?.[0]
                    || err.response?.data?.non_field_errors?.[0]
                    || 'Operation failed'
                setError(msg)
                toast.error(msg)
            })
            .finally(() => setLoading(false))
    }

    const handleDelete = () => {
        if (!activeAmenity?.id) return
        setDeleteLoading(true)

        adminServices.adminDeleteAmenity(activeAmenity.id)
            .then(() => {
                toast.success('Amenity deleted')
                onReload?.()
                updateSession()
                closeModal()
            })
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Delete failed')
            })
            .finally(() => setDeleteLoading(false))
    }

    const closeModal = () => {
        document.getElementById('amenity-modal-close-btn')?.click()
        setActiveAmenity(null)
        setError(null)
    }

    const clearForm = () => {
        setName('')
        setIconClass('')
        setDescription('')
        setActiveTab(0)
        setError(null)
    }

    return (
        <div>
            <label htmlFor="amenity-modal" id="amenity-open-btn" className="hidden">
                Amenity Modal
            </label>
            <input type="checkbox" id="amenity-modal" className="modal-toggle" onChange={clearForm} />

            <div className="modal modal-bottom sm:modal-middle">
                <div className="modal-box w-11/12 max-w-lg">
                    <label
                        htmlFor="amenity-modal"
                        id="amenity-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                        onClick={() => {
                            clearForm()
                            setActiveAmenity(null)
                        }}
                    >
                        ✕
                    </label>

                    <h3 className="text-lg font-bold mb-4">
                        {action === 0 && 'Add Amenity'}
                        {action === 1 && `Edit: ${activeAmenity?.name}`}
                        {action === 2 && `Delete: ${activeAmenity?.name}`}
                    </h3>

                    {/* Tabs */}
                    {tabs.length > 1 && (
                        <div className="flex justify-center mb-6">
                            <div className="tabs tabs-boxed">
                                {tabs.map((tab, idx) => (
                                    <button
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white' : ''}`}
                                        onClick={() => setActiveTab(idx)}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 0 && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && <FormError error={error} />}

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold">Name *</span>
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="input input-bordered w-full focus:border-primary"
                                    placeholder="e.g. Free WiFi"
                                    disabled={loading}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold">Icon</span>
                                </label>

                                <div className="flex items-center gap-3 p-3 border border-outline-variant rounded-lg bg-surface">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        {iconClass ? (
                                            <RenderIcon iconName={iconClass} className="text-primary text-xl" />
                                        ) : (
                                            <span className="text-on-surface-variant/40 text-sm">?</span>
                                        )}
                                    </div>
                                    <input
                                        type="text"
                                        value={iconClass}
                                        onChange={(e) => setIconClass(e.target.value)}
                                        className="input input-bordered w-full text-sm focus:border-primary"
                                        placeholder="e.g. BsWifi"
                                        disabled={loading}
                                    />
                                </div>

                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {ICON_PRESETS.map((icon) => (
                                        <button
                                            key={icon.name}
                                            type="button"
                                            onClick={() => setIconClass(icon.name)}
                                            className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all text-xs ${iconClass === icon.name
                                                ? 'bg-primary text-white'
                                                : 'bg-surface hover:bg-surface-container text-on-surface-variant'
                                                }`}
                                            title={icon.label}
                                            disabled={loading}
                                        >
                                            <RenderIcon iconName={icon.name} className="text-lg" />
                                            <span className="text-[9px]">{icon.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold">Description</span>
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="textarea textarea-bordered w-full min-h-[80px] focus:border-primary"
                                    placeholder="Brief description for this category..."
                                    disabled={loading}
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="submit"
                                    disabled={loading || !name.trim()}
                                    className="btn btn-primary rounded-xl w-full"
                                >
                                    {loading ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : action === 1 ? 'Update Amenity' : 'Create Amenity'}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 1 && action !== 0 && (
                        <div className="bg-error/5 border border-error/20 rounded-xl p-5">
                            <div className="flex items-start gap-3">
                                <HiTrash className="text-error h-7 w-7 mt-1" />
                                <div>
                                    <h4 className="font-bold text-error">Delete Amenity</h4>
                                    <p className="text-sm text-on-surface-variant/80 mt-1">
                                        This will remove <span className="font-semibold">{activeAmenity?.name}</span>.
                                        Properties using this amenity will no longer display it.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex gap-3">
                                <button
                                    onClick={() => setActiveTab(0)}
                                    className="btn btn-ghost btn-sm"
                                    disabled={deleteLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleteLoading}
                                    className="btn btn-error btn-sm gap-2"
                                >
                                    {deleteLoading ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <>
                                            <HiTrash className="h-4 w-4" />
                                            Confirm Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AmenityModal